/**
 * การเตรียมภาพก่อนถอด QR — ฟังก์ชันบริสุทธิ์ทั้งหมด
 * ห้าม import fs / path / sharp / canvas ที่นี่ เพราะไฟล์นี้ต้องรันในเบราว์เซอร์บนมือถือได้ด้วย (Sprint 3)
 * ทุกฟังก์ชันรับ `ImageDataLike` คืน `ImageDataLike` ใหม่ ไม่แก้ของเดิม
 */

import type { ImageDataLike } from "./types.js";

const clampByte = (v: number): number => (v < 0 ? 0 : v > 255 ? 255 : v | 0);

export function isUsableImage(img: ImageDataLike): boolean {
  return (
    Number.isInteger(img.width) &&
    Number.isInteger(img.height) &&
    img.width > 0 &&
    img.height > 0 &&
    img.data.length >= img.width * img.height * 4
  );
}

/** ย่อ/ขยายด้วยการเฉลี่ยพื้นที่ — เก็บขอบโมดูล QR ได้ดีกว่า nearest ตอนย่อ */
export function resize(img: ImageDataLike, w: number, h: number): ImageDataLike {
  const out = new Uint8ClampedArray(w * h * 4);
  const xRatio = img.width / w;
  const yRatio = img.height / h;
  for (let y = 0; y < h; y++) {
    const y0 = Math.floor(y * yRatio);
    const y1 = Math.max(y0 + 1, Math.floor((y + 1) * yRatio));
    for (let x = 0; x < w; x++) {
      const x0 = Math.floor(x * xRatio);
      const x1 = Math.max(x0 + 1, Math.floor((x + 1) * xRatio));
      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      for (let sy = y0; sy < y1 && sy < img.height; sy++) {
        for (let sx = x0; sx < x1 && sx < img.width; sx++) {
          const si = (sy * img.width + sx) * 4;
          r += img.data[si]!;
          g += img.data[si + 1]!;
          b += img.data[si + 2]!;
          n++;
        }
      }
      const di = (y * w + x) * 4;
      out[di] = n ? (r / n) | 0 : 0;
      out[di + 1] = n ? (g / n) | 0 : 0;
      out[di + 2] = n ? (b / n) | 0 : 0;
      out[di + 3] = 255;
    }
  }
  return { data: out, width: w, height: h };
}

/** ย่อให้ด้านยาวสุดไม่เกิน `maxDim` — คืนตัวเดิมถ้าเล็กอยู่แล้ว */
export function fitWithin(img: ImageDataLike, maxDim: number): ImageDataLike {
  const longest = Math.max(img.width, img.height);
  if (longest <= maxDim) return img;
  const scale = maxDim / longest;
  return resize(
    img,
    Math.max(1, Math.round(img.width * scale)),
    Math.max(1, Math.round(img.height * scale)),
  );
}

/** ขยายด้วย nearest-neighbour — ช่วยกรณี QR เล็กจนโมดูลต่ำกว่า 2 พิกเซล */
export function scaleUp(img: ImageDataLike, factor: number): ImageDataLike {
  const w = Math.max(1, Math.round(img.width * factor));
  const h = Math.max(1, Math.round(img.height * factor));
  const out = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y++) {
    const sy = Math.min(img.height - 1, (y / factor) | 0);
    for (let x = 0; x < w; x++) {
      const sx = Math.min(img.width - 1, (x / factor) | 0);
      const si = (sy * img.width + sx) * 4;
      const di = (y * w + x) * 4;
      out[di] = img.data[si]!;
      out[di + 1] = img.data[si + 1]!;
      out[di + 2] = img.data[si + 2]!;
      out[di + 3] = 255;
    }
  }
  return { data: out, width: w, height: h };
}

/** ตัดภาพ พิกัดที่ล้นขอบถูกหนีบให้อยู่ในภาพ */
export function crop(
  img: ImageDataLike,
  x: number,
  y: number,
  w: number,
  h: number,
): ImageDataLike {
  const sx = Math.max(0, Math.min(img.width - 1, x | 0));
  const sy = Math.max(0, Math.min(img.height - 1, y | 0));
  const sw = Math.max(1, Math.min(img.width - sx, w | 0));
  const sh = Math.max(1, Math.min(img.height - sy, h | 0));
  const out = new Uint8ClampedArray(sw * sh * 4);
  for (let row = 0; row < sh; row++) {
    const src = ((sy + row) * img.width + sx) * 4;
    out.set(img.data.subarray(src, src + sw * 4), row * sw * 4);
  }
  return { data: out, width: sw, height: sh };
}

/** ค่าเทาแบบ luminance (Rec. 601) เขียนกลับเป็น RGBA เพราะ jsQR รับ RGBA เท่านั้น */
export function toGray(img: ImageDataLike): ImageDataLike {
  const out = new Uint8ClampedArray(img.width * img.height * 4);
  for (let i = 0; i < out.length; i += 4) {
    const v = (img.data[i]! * 299 + img.data[i + 1]! * 587 + img.data[i + 2]! * 114) / 1000;
    const g = clampByte(v);
    out[i] = g;
    out[i + 1] = g;
    out[i + 2] = g;
    out[i + 3] = 255;
  }
  return { data: out, width: img.width, height: img.height };
}

/**
 * ยืดคอนทราสต์โดยตัดหางฮิสโทแกรมข้างละ 2 เปอร์เซ็นต์
 * แก้สลิปที่ถ่ายใต้แสงน้อย หรือมีแสงสะท้อนจนช่วงค่าความสว่างแคบ
 */
export function stretchContrast(img: ImageDataLike): ImageDataLike {
  const gray = toGray(img);
  const hist = new Uint32Array(256);
  for (let i = 0; i < gray.data.length; i += 4) hist[gray.data[i]!]!++;
  const total = (gray.data.length / 4) | 0;
  const cut = Math.max(1, (total * 0.02) | 0);

  let lo = 0;
  let acc = 0;
  while (lo < 255 && acc + hist[lo]! < cut) {
    acc += hist[lo]!;
    lo++;
  }
  let hi = 255;
  acc = 0;
  while (hi > 0 && acc + hist[hi]! < cut) {
    acc += hist[hi]!;
    hi--;
  }
  if (hi <= lo) return gray;

  const span = hi - lo;
  const out = new Uint8ClampedArray(gray.data.length);
  for (let i = 0; i < gray.data.length; i += 4) {
    const v = clampByte(((gray.data[i]! - lo) * 255) / span);
    out[i] = v;
    out[i + 1] = v;
    out[i + 2] = v;
    out[i + 3] = 255;
  }
  return { data: out, width: img.width, height: img.height };
}

/** เกณฑ์ขาวดำด้วยวิธี Otsu เกณฑ์เดียวทั้งภาพ เร็วและพอสำหรับสลิปที่แสงสม่ำเสมอ */
export function otsuBinarize(img: ImageDataLike): ImageDataLike {
  const gray = toGray(img);
  const hist = new Uint32Array(256);
  for (let i = 0; i < gray.data.length; i += 4) hist[gray.data[i]!]!++;
  const total = (gray.data.length / 4) | 0;

  let sum = 0;
  for (let t = 0; t < 256; t++) sum += t * hist[t]!;
  let sumB = 0;
  let wB = 0;
  let best = 0;
  let threshold = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t]!;
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * hist[t]!;
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > best) {
      best = between;
      threshold = t;
    }
  }

  const out = new Uint8ClampedArray(gray.data.length);
  for (let i = 0; i < gray.data.length; i += 4) {
    const v = gray.data[i]! > threshold ? 255 : 0;
    out[i] = v;
    out[i + 1] = v;
    out[i + 2] = v;
    out[i + 3] = 255;
  }
  return { data: out, width: img.width, height: img.height };
}

/**
 * เกณฑ์ขาวดำแบบปรับตามพื้นที่ (Sauvola บน integral image)
 * ใช้กับแสงสะท้อนเป็นหย่อม ซึ่งเกณฑ์เดียวทั้งภาพเอาไม่อยู่
 */
export function adaptiveBinarize(img: ImageDataLike, window = 31, k = 0.2): ImageDataLike {
  const gray = toGray(img);
  const w = gray.width;
  const h = gray.height;
  const stride = w + 1;
  const S = new Float64Array(stride * (h + 1));
  const S2 = new Float64Array(stride * (h + 1));

  for (let y = 1; y <= h; y++) {
    let rowSum = 0;
    let rowSq = 0;
    for (let x = 1; x <= w; x++) {
      const v = gray.data[((y - 1) * w + (x - 1)) * 4]!;
      rowSum += v;
      rowSq += v * v;
      S[y * stride + x] = S[(y - 1) * stride + x]! + rowSum;
      S2[y * stride + x] = S2[(y - 1) * stride + x]! + rowSq;
    }
  }

  const r = Math.max(1, window >> 1);
  const out = new Uint8ClampedArray(gray.data.length);
  for (let y = 0; y < h; y++) {
    const y0 = Math.max(0, y - r);
    const y1 = Math.min(h - 1, y + r);
    for (let x = 0; x < w; x++) {
      const x0 = Math.max(0, x - r);
      const x1 = Math.min(w - 1, x + r);
      const area = (x1 - x0 + 1) * (y1 - y0 + 1);
      const mean =
        (S[(y1 + 1) * stride + x1 + 1]! -
          S[y0 * stride + x1 + 1]! -
          S[(y1 + 1) * stride + x0]! +
          S[y0 * stride + x0]!) /
        area;
      const meanSq =
        (S2[(y1 + 1) * stride + x1 + 1]! -
          S2[y0 * stride + x1 + 1]! -
          S2[(y1 + 1) * stride + x0]! +
          S2[y0 * stride + x0]!) /
        area;
      const std = Math.sqrt(Math.max(0, meanSq - mean * mean));
      const threshold = mean * (1 + k * (std / 128 - 1));
      const i = (y * w + x) * 4;
      const v = gray.data[i]! > threshold ? 255 : 0;
      out[i] = v;
      out[i + 1] = v;
      out[i + 2] = v;
      out[i + 3] = 255;
    }
  }
  return { data: out, width: w, height: h };
}

/** แบ่งภาพเป็นตาราง cols x rows โดยให้ช่องซ้อนกัน เพื่อไม่ให้ QR ถูกตัดกลางพอดี */
export function tiles(
  img: ImageDataLike,
  cols: number,
  rows: number,
  overlap = 0.25,
): ImageDataLike[] {
  const tw = Math.ceil(img.width / cols);
  const th = Math.ceil(img.height / rows);
  const ox = Math.round(tw * overlap);
  const oy = Math.round(th * overlap);
  const out: ImageDataLike[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      out.push(crop(img, c * tw - ox, r * th - oy, tw + 2 * ox, th + 2 * oy));
    }
  }
  return out;
}
